<template>
    <div :style="{ marginBottom: normalizeSizeVertical(20, true) }">
        <!-- Section Header -->
        <div v-if="isManagePage" class="ppv2-mb-8">
            <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-2">
                <span
                    class="ppv2-inline-block"
                    :style="{ width: '18px', height: '2px', background: 'var(--pp-primary-500, #7D2DBD)' }"
                ></span>
                <span
                    class="poppinsSemiBold ppv2-uppercase ppv2-tracking-wider"
                    :style="{ fontSize: normalizeFontSize(10), color: 'var(--pp-primary-500, #7D2DBD)' }"
                >
                    {{ $t('subscription.manage.table_badge') }}
                </span>
            </div>
            <div class="ppv2-flex ppv2-items-baseline ppv2-justify-between">
                <h2 class="poppinsSemiBold ppv2-text-black ppv2-mb-0" :style="{ fontSize: normalizeFontSize(20) }">
                    {{ $t('subscription.manage.table_title') }}
                </h2>
            </div>
            <div class="ppv2-border-b ppv2-border-gray-200 ppv2-mt-4 ppv2-mx-[-32px]"></div>
        </div>

        <!-- Desktop Features List -->
        <div class="ppv2-hidden md:ppv2-block">
            <!-- Table Header -->
            <div
                ref="stickyHeader"
                class="ppv2-grid ppv2-grid-cols-4 md:ppv2-grid-cols-5 ppv2-gap-3 ppv2-mb-0 ppv2-sticky ppv2-top-0 ppv2-z-30 ppv2-bg-white dark:ppv2-bg-dark"
            >
                <div class="ppv2-hidden md:ppv2-flex ppv2-items-end ppv2-justify-end">
                    <div
                        class="ppv2-text-gray-500 poppinsRegular ppv2-pt-0 ppv2-px-4 ppv2-pb-7 ppv2-mb-2"
                        :style="{ fontSize: normalizeFontSize(12) }"
                    >
                        {{ $t('subscription.common.monthly_ht') }}
                    </div>
                </div>
                <div
                    v-for="(plan, index) in pricingPlans"
                    :key="plan.key"
                    :class="[
                        'ppv2-text-center ppv2-w-full ppv2-mx-auto ppv2-relative ppv2-h-full ppv2-flex ppv2-flex-col ppv2-justify-end',
                        index === 0 ? 'ppv2-mt-0' : 'ppv2-mt-[12px]',
                    ]"
                >
                    <div
                        v-if="currentPlanKey === plan.key"
                        class="ppv2-absolute ppv2-top-[-20px] ppv2-left-0 ppv2-right-0 ppv2-text-center"
                        style="line-height: 1"
                    >
                        <span class="ppv2-text-[#8241D7] poppinsSemiBold ppv2-text-[11px] ppv2-uppercase tracking-widest">
                            {{ $t('subscription.manage.current_offer_badge') || 'OFFRE ACTUELLE' }}
                        </span>
                    </div>
                    <div
                        :class="{
                            'current-plan-header ppv2-top-3 ppv2-relative': currentPlanKey === plan.key,
                            'ppv2-top-3 ppv2-relative': currentPlanKey !== plan.key,
                            'full-height-after-free': plan.key === 'free' && currentPlanKey === 'free',
                        }"
                    >
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-mb-2">
                            <h4 class="poppinsBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(20) }">
                                {{ $t('subscription.plans.' + plan.key + '.name') }}
                            </h4>
                            <span
                                v-if="plan.key === 'pro'"
                                class="ppv2-bg-[#5D3FBD] ppv2-px-3 ppv2-py-1 ppv2-rounded-full ppv2-text-white poppinsMedium"
                                :style="{ fontSize: normalizeFontSize(11) }"
                            >
                                {{ $t('subscription.plans.pro.badge') || 'Populaire' }}
                            </span>
                        </div>

                        <div class="ppv2-flex ppv2-items-baseline ppv2-justify-center ppv2-gap-1 ppv2-mb-2">
                            <div class="poppinsBold ppv2-text-black" :style="{ fontSize: normalizeFontSize(30, true) }">
                                {{ $t('subscription.common.' + plan.key + '_price') }}
                            </div>
                        </div>

                        <button
                            v-if="currentPlanKey !== 'free' && plan.key === currentPlanKey && !canContinueFree"
                            @click="$emit(isCanceling ? 'resume-click' : 'cancel-click')"
                            :style="{ fontSize: normalizeSize(13.5) }"
                            class="ppv2-w-[90%] ppv2-py-2 ppv2-rounded-xl ppv2-mb-3 poppinsBold ppv2-bg-white dark:ppv2-bg-dark ppv2-text-black ppv2-border ppv2-border-[#D9DBE9] ppv2-transition-all ppv2-shadow-sm"
                        >
                            {{
                                isCanceling
                                    ? $t('subscription.manage.resume_subscription')
                                    : $t('subscription.manage.cancel_subscription')
                            }}
                        </button>
                        <button
                            v-else-if="canContinueFree && plan.key === currentPlanKey"
                            :style="getButtonStyle(plan)"
                            class="ppv2-w-full ppv2-py-2 ppv2-rounded-xl ppv2-mb-3 poppinsMedium ppv2-transition-all ppv2-shadow-sm"
                            @click="$emit('plan-click', plan.key)"
                        >
                            {{ getButtonText(plan) }}
                        </button>
                        <div
                            v-else-if="plan.key === 'free' && currentPlanKey !== 'free'"
                            class="ppv2-h-[37px] ppv2-flex ppv2-items-center ppv2-justify-center"
                        >
                            <!--span class="poppinsMedium ppv2-text-gray-400 ppv2-text-sm">{{
                                $t('subscription.plans.free.button') || 'Gratuit'
                            }}</span-->
                        </div>
                        <button
                            v-else-if="getPlanLevel(plan.key) > getPlanLevel(currentPlanKey)"
                            :style="getButtonStyle(plan)"
                            class="ppv2-w-[90%] ppv2-py-2 ppv2-rounded-lg ppv2-mb-3 poppinsMedium ppv2-transition-all ppv2-shadow-sm"
                            @click="$emit('plan-click', plan.key)"
                        >
                            {{ getButtonText(plan) }}
                        </button>
                        <button
                            v-else-if="getPlanLevel(plan.key) < getPlanLevel(currentPlanKey)"
                            :style="getButtonStyle(plan)"
                            class="ppv2-w-[90%] ppv2-py-2 ppv2-rounded-xl ppv2-mb-3 poppinsBold ppv2-transition-all ppv2-shadow-sm"
                            @click="$emit('plan-click', plan.key)"
                        >
                            {{ getButtonText(plan) }}
                        </button>
                        <div
                            v-else-if="plan.key === 'free' && currentPlanKey === 'free'"
                            class="ppv2-h-[37px] ppv2-flex ppv2-items-center ppv2-justify-center"
                        >
                            <!--span class="poppinsMedium ppv2-text-green-500 ppv2-text-sm">{{
                                $t('subscription.common.current_plan')
                            }}</span-->
                        </div>
                        <div v-else class="ppv2-h-12"></div>
                        <!-- Spacer for active plan -->
                    </div>
                </div>
            </div>
            <div v-for="(category, catIndex) in featureCategories" :key="catIndex" class="ppv2-mb-0">
                <div
                    class="ppv2-grid ppv2-grid-cols-5 ppv2-gap-3 ppv2-border-b ppv2-border-[#D9DBE9] ppv2-sticky ppv2-z-20 ppv2-bg-white dark:ppv2-bg-dark"
                    :style="{ top: headerHeight - 2 + 'px' }"
                >
                    <h3
                        class="poppinsBold ppv2-text-black ppv2-mb-0"
                        :style="{
                            fontSize: normalizeFontSize(18),
                            paddingTop: normalizeSizeVertical(32, true),
                            paddingBottom: normalizeSizeVertical(32, true),
                        }"
                    >
                        {{ $t('subscription.table.categories.' + category.key) }}
                    </h3>
                    <div
                        v-for="planKey in ['free', 'essentiel', 'pro', 'premium']"
                        :key="'cat-' + planKey"
                        class="ppv2-h-full"
                        :class="currentPlanKey === planKey ? 'hasAbonnement ppv2-relative' : ''"
                    ></div>
                </div>

                <!-- Features -->
                <div
                    v-for="(feature, featIndex) in category.features"
                    :key="featIndex"
                    class="ppv2-grid ppv2-grid-cols-5 ppv2-gap-3 ppv2-border-b ppv2-border-[#D9DBE9] ppv2-items-stretch"
                >
                    <div
                        class="poppinsMedium ppv2-text-black ppv2-text-opacity-80 ppv2-py-4 ppv2-flex ppv2-items-center"
                        :style="{
                            fontSize: normalizeFontSize(15),
                        }"
                    >
                        {{ $t('subscription.table.features.' + feature.key) }}
                    </div>

                    <!-- Plan columns -->
                    <div
                        v-for="planKey in ['free', 'essentiel', 'pro', 'premium']"
                        :key="planKey"
                        class="ppv2-text-center ppv2-relative ppv2-py-4 ppv2-flex ppv2-items-center ppv2-justify-center"
                        :class="currentPlanKey === planKey ? 'bg-purple ppv2-bg-opacity-10' : ''"
                    >
                        <div
                            v-if="currentPlanKey === planKey"
                            class="active-plan-side-borders"
                            :class="{
                                'is-last-row':
                                    catIndex === featureCategories.length - 1 && featIndex === category.features.length - 1,
                            }"
                        ></div>

                        <template v-if="typeof feature.plans[planKey] === 'boolean'">
                            <div
                                v-if="feature.plans[planKey]"
                                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-full ppv2-bg-[#5338FC] ppv2-flex ppv2-items-center ppv2-justify-center"
                            >
                                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 5L5 9L13 1"
                                        stroke="white"
                                        stroke-width="2.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                            <div
                                v-else
                                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-full ppv2-bg-neutral-200 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-[#000]"
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </div>
                        </template>
                        <span
                            v-else
                            class="poppinsMedium ppv2-text-black ppv2-text-opacity-60"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            {{ translateValue(feature.plans[planKey]) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile Features Accordion Layout -->
        <div class="ppv2-block md:ppv2-hidden">
            <div v-for="category in featureCategories" :key="'mob-cat-' + category.key" class="ppv2-mb-8">
                <h3
                    class="poppinsBold ppv2-text-xl ppv2-mb-4 ppv2-text-black dark:ppv2-text-white ppv2-sticky ppv2-top-0 ppv2-z-20 ppv2-bg-white dark:ppv2-bg-dark ppv2-py-2"
                >
                    {{ $t('subscription.table.categories.' + category.key) }}
                </h3>

                <div class="ppv2-border-t ppv2-border-gray-200 dark:ppv2-border-gray-700">
                    <div
                        v-for="feature in category.features"
                        :key="'mob-feat-' + feature.key"
                        class="ppv2-border-b ppv2-border-gray-200 dark:ppv2-border-gray-700"
                    >
                        <button
                            @click="toggleFeature(feature.key)"
                            class="ppv2-w-full ppv2-flex ppv2-justify-between ppv2-items-center ppv2-py-4 ppv2-text-left ppv2-bg-transparent ppv2-border-0"
                            style="cursor: pointer"
                        >
                            <span class="poppinsMedium ppv2-text-base ppv2-text-gray-700 dark:ppv2-text-gray-300">
                                {{ $t('subscription.table.features.' + feature.key) }}
                            </span>
                            <svg
                                :class="{ 'ppv2-rotate-180': (expandedFeatures || {})[feature.key] }"
                                class="ppv2-w-5 ppv2-h-5 ppv2-transition-transform ppv2-text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <transition name="accordion-slide">
                            <div v-show="(expandedFeatures || {})[feature.key]" class="ppv2-pb-4 ppv2-overflow-hidden">
                                <div
                                    v-for="planKey in ['free', 'essentiel', 'pro', 'premium']"
                                    :key="'mob-val-' + feature.key + '-' + planKey"
                                    class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-p-2 ppv2-border-t ppv2-border-gray-100 dark:ppv2-border-gray-800"
                                    :class="currentPlanKey === planKey ? 'bg-purple ppv2-bg-opacity-10' : ''"
                                >
                                    <span
                                        class="poppinsMedium ppv2-text-sm"
                                        :class="
                                            currentPlanKey === planKey
                                                ? 'ppv2-text-[#8241D7]'
                                                : 'ppv2-text-gray-500 dark:ppv2-text-gray-400'
                                        "
                                    >
                                        {{ $t('subscription.plans.' + planKey + '.name') }}
                                        <span
                                            v-if="currentPlanKey === planKey"
                                            class="ppv2-ml-1 ppv2-text-[10px] ppv2-uppercase ppv2-tracking-wider ppv2-opacity-70"
                                            >({{ $t('subscription.common.current_plan') || 'Actuel' }})</span
                                        >
                                    </span>

                                    <div class="poppinsMedium ppv2-text-sm ppv2-text-gray-900 dark:ppv2-text-white">
                                        <template v-if="typeof feature.plans[planKey] === 'boolean'">
                                            <div
                                                v-if="feature.plans[planKey]"
                                                class="ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-bg-[#5338FC] ppv2-flex ppv2-items-center ppv2-justify-center"
                                            >
                                                <svg
                                                    width="12"
                                                    height="8"
                                                    viewBox="0 0 14 10"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M1 5L5 9L13 1"
                                                        stroke="white"
                                                        stroke-width="2.5"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                            <div
                                                v-else
                                                class="ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-bg-neutral-200 dark:ppv2-bg-gray-700 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-gray-500 dark:ppv2-text-gray-400"
                                            >
                                                <svg
                                                    width="10"
                                                    height="10"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="3"
                                                >
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                            </div>
                                        </template>
                                        <span v-else>
                                            {{ translateValue(feature.plans[planKey]) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import { pricingPlans, featureCategories } from './constants'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'SubscriptionTable',
    props: {
        canContinueFree: {
            type: Boolean,
            default: false,
        },
        currentPlanKey: {
            type: String,
            default: null,
        },
        isManagePage: {
            type: Boolean,
            default: false,
        },
        // Résiliation programmée : le bouton de l'offre en cours propose de la lever
        // plutôt que de re-résilier.
        isCanceling: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            headerHeight: 220,
            pricingPlans,
            featureCategories,
            expandedFeatures: {},
        }
    },
    methods: {
        toggleFeature(key) {
            this.$set(this.expandedFeatures, key, !this.expandedFeatures[key])
        },
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        translateValue(value) {
            if (typeof value === 'boolean') return value
            return this.$t('subscription.common.' + value)
        },
        updateHeaderHeight() {
            if (this.$refs.stickyHeader) {
                this.headerHeight = this.$refs.stickyHeader.offsetHeight
            }
        },
        getPlanLevel(key) {
            const levels = { free: 0, essentiel: 1, pro: 2, premium: 3 }
            return levels[key] || 0
        },
        getButtonText(plan) {
            if (this.canContinueFree && plan.key === this.currentPlanKey) {
                return this.$t('subscription.common.activate_plan', { plan: this.$t('subscription.plans.' + plan.key + '.name') })
            }
            if (this.isManagePage) {
                const currentLevel = this.getPlanLevel(this.currentPlanKey)
                const planLevel = this.getPlanLevel(plan.key)

                if (planLevel > currentLevel) {
                    const planName = this.$t('subscription.plans.' + plan.key + '.name')
                    return this.$t('subscription.manage.upgrade_to', { plan: planName })
                }

                if (planLevel < currentLevel) {
                    return this.$t('subscription.manage.downgrade')
                }

                return this.$t('subscription.manage.change_offer') || "Changer d'offre"
            }
            return this.$t('subscription.plans.' + plan.key + '.button')
        },
        getButtonStyle(plan) {
            const baseStyle = {
                fontSize: normalizeFontSize(13.5),
                paddingLeft: normalizeSize(13),
                paddingRight: normalizeSize(13),
            }

            const isUpgrade = this.getPlanLevel(plan.key) > this.getPlanLevel(this.currentPlanKey)
            const isActivate = this.canContinueFree && plan.key === this.currentPlanKey

            if (isUpgrade || isActivate) {
                return {
                    ...baseStyle,
                    background: 'linear-gradient(90deg, #8241D7 0%, #3A329D 100%)',
                    color: 'white',
                    boxShadow: '0px 4px 6px 0px #FFFFFF66 inset, 0px -2px 2px 0px #1B235512 inset, 0px 3px 6px 0px #07006E08',
                }
            }

            return {
                ...baseStyle,
                border: '1px solid #D9DBE9',
                boxShadow: '0px 4px 6px 0px #FFFFFF66 inset, 0px -2px 2px 0px #1B235512 inset, 0px 3px 6px 0px #07006E08',
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.updateHeaderHeight)
        this.$nextTick(() => {
            this.updateHeaderHeight()
        })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateHeaderHeight)
    },
}
</script>

<style scoped>
.current-plan-header::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -1px;
    right: -1px;
    height: calc(100% - 12px);
    border-left: 2.5px solid #8241d7;
    border-right: 2.5px solid #8241d7;
    border-top: 2.5px solid #8241d7;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    background-color: #7d2dbd1a;
    z-index: -1;
    pointer-events: none;
}
.full-height-after-free::after {
    height: 100% !important;
}
.hasAbonnement:after {
    content: '';
    top: 0;
    left: -1px;
    right: -1px;
    height: 100%;
    border-left: 2.5px solid #8241d7;
    border-right: 2.5px solid #8241d7;
    background-color: #7d2dbd1a;
    position: absolute;
    pointer-events: none;
}
.active-plan-side-borders {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -1px;
    right: -1px;
    border-left: 2.5px solid #8241d7;
    border-right: 2.5px solid #8241d7;
    pointer-events: none;
    z-index: 10;
}
.active-plan-side-borders.is-last-row {
    border-bottom: 2.5px solid #8241d7;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
}
</style>
